'use client';

import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface HeroSectionProps {
  currentLanguage: string;
}

const HeroSection = ({ currentLanguage }: HeroSectionProps) => {
  const content = {
    en: {
      badge: 'Regulatory Compliance',
      title: 'Your Comprehensive BRSR & ESG Compliance Hub',
      subtitle: 'Navigate complex regulatory frameworks with confidence',
      description:
        'Streamline your journey through BRSR SEBI India, CSRD Europe, and industry-specific reporting requirements. AI-powered gap analysis, real-time compliance monitoring, and NetZero roadmap tracking—all in one unified platform.',
      cta: 'Explore Compliance Pathways',
      secondaryCta: 'Schedule Expert Consultation',
      features: [
        'Multi-framework compliance tracking',
        'AI-powered gap analysis',
        'NetZero roadmap (2030/2047/2074)',
        'Real-time regulatory updates',
      ],
    },
    hi: {
      badge: 'नियामक अनुपालन',
      title: 'आपका व्यापक BRSR और ESG अनुपालन केंद्र',
      subtitle: 'जटिल नियामक ढांचे में आत्मविश्वास के साथ नेविगेट करें',
      description:
        'BRSR SEBI India, CSRD यूरोप, और उद्योग-विशिष्ट रिपोर्टिंग आवश्यकताओं के माध्यम से अपनी यात्रा को सुव्यवस्थित करें। AI-संचालित गैप विश्लेषण, वास्तविक समय अनुपालन निगरानी, और NetZero रोडमैप ट्रैकिंग—सभी एक एकीकृत मंच में।',
      cta: 'अनुपालन मार्ग देखें',
      secondaryCta: 'विशेषज्ञ परामर्श निर्धारित करें',
      features: [
        'मल्टी-फ्रेमवर्क अनुपालन ट्रैकिंग',
        'AI-संचालित गैप विश्लेषण',
        'NetZero रोडमैप (2030/2047/2074)',
        'वास्तविक समय नियामक अपडेट',
      ],
    },
    ar: {
      badge: 'الامتثال التنظيمي',
      title: 'مركز الامتثال الشامل لـ BRSR و ESG',
      subtitle: 'التنقل في الأطر التنظيمية المعقدة بثقة',
      description:
        'قم بتبسيط رحلتك عبر BRSR SEBI الهند و CSRD أوروبا ومتطلبات الإبلاغ الخاصة بالصناعة. تحليل الفجوات المدعوم بالذكاء الاصطناعي ومراقبة الامتثال في الوقت الفعلي وتتبع خارطة طريق NetZero - كل ذلك في منصة واحدة موحدة.',
      cta: 'استكشف مسارات الامتثال',
      secondaryCta: 'جدولة استشارة خبير',
      features: [
        'تتبع الامتثال متعدد الأطر',
        'تحليل الفجوات بالذكاء الاصطناعي',
        'خارطة طريق NetZero (2030/2047/2074)',
        'تحديثات تنظيمية في الوقت الفعلي',
      ],
    },
  };

  const t = content[currentLanguage as keyof typeof content] || content.en;

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-secondary/5 to-accent/5 pt-24 pb-20">
      <div className="absolute inset-0 bg-grid-pattern opacity-5" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-4xl mx-auto mb-12">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-6">
            <Icon name="ShieldCheckIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">{t.badge}</span>
          </div>

          <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-text-primary mb-6 leading-tight">
            {t.title}
          </h1>

          <p className="text-xl text-primary font-semibold mb-4">{t.subtitle}</p>

          <p className="text-lg text-text-secondary leading-relaxed mb-8 max-w-3xl mx-auto">
            {t.description}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Link
              href="#compliance-navigator"
              className="w-full sm:w-auto px-8 py-4 bg-conversion text-conversion-foreground font-heading font-semibold text-base rounded-lg hover:bg-conversion/90 shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic transform hover:scale-102"
            >
              {t.cta}
            </Link>
            <Link
              href="/get-started"
              className="w-full sm:w-auto px-8 py-4 bg-card text-text-primary border-2 border-primary font-heading font-semibold text-base rounded-lg hover:bg-primary hover:text-primary-foreground shadow-natural hover:shadow-natural-lg transition-all duration-300 ease-organic"
            >
              {t.secondaryCta}
            </Link>
          </div>

          {/* Key Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {t.features.map((feature, index) => (
              <div
                key={index}
                className="bg-card border border-border rounded-lg p-4 hover:shadow-natural transition-all duration-300"
              >
                <div className="flex items-start space-x-3">
                  <Icon name="CheckCircleIcon" size={20} className="text-success mt-1 flex-shrink-0" />
                  <p className="text-sm font-medium text-text-primary text-left">{feature}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Compliance Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-16">
          {[
            { value: '15+', label: 'Regulatory Frameworks' },
            { value: '8', label: 'Industry Sectors' },
            { value: '24/7', label: 'Monitoring' },
            { value: '100+', label: 'Compliance Metrics' },
          ].map((stat, index) => (
            <div key={index} className="text-center">
              <div className="text-3xl md:text-4xl font-heading font-bold text-primary mb-2">
                {stat.value}
              </div>
              <div className="text-sm text-text-secondary">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;